import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, Observable, shareReplay, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { MOCK_PRODUCT_TOKEN, MockProduct } from '@libs/configs/calculate.token';
import { IDataLayer, IScheduleEvent } from '@libs/models/models';
import { ICalculatePreview } from '@libs/interfaces/calculate-preview.interface';
import { environment } from '@libs/environment';
import { DATA_LAYER, ENVIRONMENT, SESSION_STORAGE, WINDOW } from '../../injections/window';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CalculateStore } from '@libs/shared/storage/calculate.store';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { EnvironmentType } from '@libs/models/environment.type';

@Component({
  selector: 'app-calculate',
  templateUrl: `./templates/${environment.name}/template.html`,
  styleUrls: [`./templates/${environment.name}/style.sass`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculateComponent implements OnInit, OnDestroy {
  @Input()
  public color: 'white' | 'colorful' = 'white';

  @Input()
  public size: 'small' | 'big' = 'small';

  @Input()
  public isDetailsOutside: boolean = false;

  public amount: number;
  public term: number;
  public activeTab: string = '';
  public preview$: Observable<ICalculatePreview> = this.calculateStore.preview$.pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );
  public schedule$: Observable<Array<IScheduleEvent>> = this.calculateStore.schedule$.pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );
  public isShowPromo: boolean = false;
  public form: FormGroup;
  public MIN_CODE_LENGTH: number = 6;
  public translateParamsStar: Record<string, string>;
  private documentClick$: Subscription;

  constructor(
    @Inject(MOCK_PRODUCT_TOKEN) public mockProduct: MockProduct,
    @Inject(WINDOW) public window: Window,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    @Inject(DATA_LAYER) private dataLayer: IDataLayer[],
    @Inject(ENVIRONMENT) private environment: EnvironmentType,
    private cd: ChangeDetectorRef,
    private eRef: ElementRef,
    private ngZone: NgZone,
    private router: Router,
    private fb: FormBuilder,
    private calculateStore: CalculateStore,
    private cookieService: CookieService,
  ) {
    this.translateParamsStar = {
      first: this.environment?.publicDocumentsUrl + 'приклади_розрахунку_калькулятора.pdf',
      second: this.environment?.publicDocumentsUrl + 'Calc.xlsx',
    };
    this.calculateStore.setProduct(this.mockProduct);
  }

  public ngOnInit(): void {
    this.initForm(this.mockProduct);
    this.ngZone.runOutsideAngular(() => {
      this.documentClick$ = fromEvent(document, 'click', { capture: true })
        .pipe(throttleTime(50))
        .subscribe((event: Event) => {
          if (this.activeTab && event.target !== this.eRef.nativeElement && !this.eRef.nativeElement.contains(event.target)) {
            this.activeTab = '';
            this.cd.detectChanges();
          }
        });
    });
  }

  public ngOnDestroy(): void {
    if (this.documentClick$) {
      this.documentClick$.unsubscribe();
    }
  }

  public showDetails(type: 'SCHEDULE' | 'DETAILS'): void {
    this.activeTab = type !== this.activeTab ? type : '';
  }

  public calculateChangeHandler($event: { amount: number; term: number; interest: number }): void {
    this.amount = $event.amount;
    this.term = $event.term;
    this.calculateStore.updateFakePreview($event.amount, $event.term, $event.interest);
    this.calculateStore.updateFakeSchedule($event.amount, $event.term, this.mockProduct.interest);
  }

  public paymentsQuantity(term: number): number {
    return Math.round(this.mockProduct.fullTerm / term);
  }

  public showPromo(): void {
    this.isShowPromo = !this.isShowPromo;
    this.cd.detectChanges();
  }

  public navigateToCreditUrl(): void {
    const promoCode = this.form.get('code').value ?? '';
    const totalAmount = this.calculateStore.getTotalAmountValue.toString();

    this.trackCalculatorEvent(this.term, promoCode, this.amount);
    this.saveSessionData(this.amount, this.term, promoCode, totalAmount);
    this.saveCookieData(this.amount, this.term);

    this.router.navigate(['auth', 'login']);
  }

  private trackCalculatorEvent(term: number, promoCode: string, amount: number): void {
    this.dataLayer.push({
      event: 'calculator_click_button',
      text: 'Отримати гроші',
      desired_term: `${term}`,
      promo_code: promoCode || false,
      value: amount,
    });
  }

  private saveSessionData(amount: number, term: number, promoCode: string, totalAmount: string): void {
    const sessionData = {
      amount: amount.toString(),
      term: term.toString(),
      promoCode,
      totalAmount,
    };

    Object.keys(sessionData).forEach((key) => {
      this.sessionStorage.setItem(key, sessionData[key]);
    });
  }

  private saveCookieData(amount: number, term: number): void {
    this.cookieService.set(
      'use_default_landing_amount',
      `${this.mockProduct.startAmount === amount}`,
      5,
      '/',
      this.environment.cookieDomain,
    );
    this.cookieService.set('use_default_landing_term', `${this.mockProduct.startTerm === term}`, 5, '/', this.environment.cookieDomain);
  }

  private initForm(mockProduct: MockProduct): void {
    this.amount = this.calculateStore.amount(mockProduct);
    this.term = this.calculateStore.term(mockProduct);

    this.form = this.fb.group({
      calculate: this.fb.group({
        amount: [this.amount],
        term: [this.term],
      }),
      code: [null],
    });

    this.calculateStore.updateFakePreview(this.amount, this.term, mockProduct.interest);
    this.calculateStore.updateFakeSchedule(this.amount, this.term, mockProduct.interest);
  }
}
